import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button, Modal, Spinner, Form, Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { BsPencil, BsTrash, BsImage, BsThreeDotsVertical } from 'react-icons/bs';
import {
  FaFacebook, FaYoutube, FaInstagram, FaTiktok,
  FaTwitter, FaGlobe, FaLink, FaChevronDown, FaChevronUp,
} from 'react-icons/fa';
import { useSelector } from 'react-redux';
import StarDivider from '../components/uiComponents/StarDivider';
import DeleteModal from '../components/DeleteModal';
import Toast from '../components/Toast';
import Pagination from '../components/Pagination';
import channelService from '../api/channelService';
import { uploadFile } from '../api/uploadService';

const PLATFORM_OPTIONS = [
  { value: 'facebook', label: 'Facebook', Icon: FaFacebook, bg: '#dce8f7', color: '#1877F2' },
  { value: 'youtube', label: 'YouTube', Icon: FaYoutube, bg: '#fde8e8', color: '#FF0000' },
  { value: 'instagram', label: 'Instagram', Icon: FaInstagram, bg: '#fce4f5', color: '#C13584' },
  { value: 'tiktok', label: 'TikTok', Icon: FaTiktok, bg: '#e8f7f7', color: '#010101' },
  { value: 'twitter', label: 'Twitter/X', Icon: FaTwitter, bg: '#e8f4fd', color: '#1DA1F2' },
  { value: 'website', label: 'Site Web', Icon: FaGlobe, bg: '#e8f5e9', color: '#388E3C' },
  { value: 'other', label: 'Autre', Icon: FaLink, bg: '#f5f5f5', color: '#666' },
];

const EMPTY_FORM = { nom_canal: '', url_connexion: '', description: '', platform_type: 'facebook', statut: 'active' };

function getPlatform(val) {
  return PLATFORM_OPTIONS.find((p) => p.value === val) || PLATFORM_OPTIONS[6];
}

function extractError(err) {
  if (err?.response?.data?.errors?.length)
    return err.response.data.errors.map((e) => e.message).join(', ');
  return err?.response?.data?.message || err?.message || 'Une erreur est survenue';
}

function isValidHttpUrl(value) {
  try {
    const url = new URL(value.trim());
    return (url.protocol === 'http:' || url.protocol === 'https:') && Boolean(url.hostname);
  } catch {
    return false;
  }
}

export default function CanauxPage() {
  const isViewer = useSelector((s) => s.auth.user?.role) === 'viewer';
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1, currentPage: 1 });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [iconFile, setIconFile] = useState(null);
  const [iconPreview, setIconPreview] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [toast, setToast] = useState({ message: '', type: 'success' });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const fileRef = useRef();

  // Filters + pagination
  const [filters, setFilters] = useState({ platform_type: '', nom_canal: '', statut: '', page: 1, limit: 20 });

  const handleClearFilters = () => {
    setFilters({ platform_type: '', nom_canal: '', statut: '', page: 1, limit: 20 });
  };

  const showToast = (msg, type = 'success') => setToast({ message: msg, type });
  const hideToast = () => setToast({ message: '', type: 'success' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: filters.page, limit: filters.limit };
      if (filters.platform_type) params.platform_type = filters.platform_type;
      if (filters.nom_canal) params.nom_canal = filters.nom_canal;
      if (filters.statut) params.statut = filters.statut;
      const { data: res } = await channelService.list(params);
      setData(res.data);
      setPagination(res.pagination || { total: 0, totalPages: 1, currentPage: 1 });
    } catch (err) {
      showToast(extractError(err), 'danger');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleFilterChange = (e) =>
    setFilters((f) => ({ ...f, [e.target.name]: e.target.value, page: 1 }));

  const setF = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setFormErrors((fe) => ({ ...fe, [k]: '' }));
  };

  const handleIconChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIconFile(file);
    setIconPreview(URL.createObjectURL(file));
  };

  const validateForm = () => {
    const errors = {};
    if (!form.nom_canal) errors.nom_canal = 'Nom du canal requis';
    if (!form.url_connexion) errors.url_connexion = 'URL requise';
    else if (!isValidHttpUrl(form.url_connexion)) {
      errors.url_connexion = 'Veuillez saisir une URL valide commençant par http:// ou https://';
    }
    return errors;
  };

  const handleSave = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length) { setFormErrors(errors); return; }

    setSaving(true);
    setUploadProgress(0);

    try {
      if (editItem) {
        // Build a patch with only changed scalar fields
        const patch = {};
        const scalarFields = ['nom_canal', 'url_connexion', 'platform_type', 'statut'];
        scalarFields.forEach((key) => {
          if (form[key] !== (editItem[key] ?? '')) patch[key] = form[key];
        });

        // description: treat null/undefined/'' as equivalent
        const prevDesc = editItem.description ?? '';
        const nextDesc = form.description ?? '';
        if (nextDesc !== prevDesc) patch.description = nextDesc || null;

        // icon: only include if a new file was selected
        if (iconFile) {
          const { publicUrl } = await uploadFile('channels', iconFile, (p) => setUploadProgress(p));
          patch.iconeUrl = publicUrl;
        }

        if (Object.keys(patch).length === 0) {
          showToast('Aucune modification détectée.');
          return;
        }

        await channelService.update(editItem.id, patch);
        showToast('Canal mis à jour avec succès');
        setEditItem(null);
      } else {
        const payload = {
          nom_canal: form.nom_canal,
          url_connexion: form.url_connexion,
          platform_type: form.platform_type,
          description: form.description || undefined,
          statut: form.statut || 'active',
        };

        if (iconFile) {
          const { publicUrl } = await uploadFile('channels', iconFile, (p) => setUploadProgress(p));
          payload.iconeUrl = publicUrl;
        }

        await channelService.create(payload);
        showToast('Canal ajouté avec succès');
        setShowAdd(false);
      }

      resetForm();
      fetchData();
    } catch (err) {
      showToast(extractError(err), 'danger');
    } finally {
      setSaving(false);
      setUploadProgress(0);
    }
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setIconFile(null);
    setIconPreview(null);
    setFormErrors({});
    setUploadProgress(0);
  };

  const handleEditOpen = (item) => {
    setForm({
      nom_canal: item.nom_canal,
      url_connexion: item.url_connexion,
      description: item.description || '',
      platform_type: item.platform_type,
      statut: item.statut || 'active',
    });
    setIconPreview(item.icone_url || null);
    setIconFile(null);
    setEditItem(item);
    setFormErrors({});
  };

  const handleDelete = async () => {
    try {
      await channelService.remove(deleteTarget.id);
      showToast('Canal supprimé avec succès');
      setDeleteTarget(null);
      fetchData();
    } catch (err) {
      showToast(extractError(err), 'danger');
      setDeleteTarget(null);
    }
  };


  return (
    <div>
      <Toast message={toast.message} type={toast.type} onClose={hideToast} />

      <div className="text-center mb-4">
        <h5 style={{ fontFamily: 'serif', letterSpacing: 3, fontWeight: 400, fontSize: 'clamp(12px,2vw,18px)' }}>
          ADMINISTRATIONS DES CANAUX DE COMMUNICATION
        </h5>
        <StarDivider lineWidth="5%" />
      </div>

      {/* Filters */}
      <div className="bg-white p-3 rounded mb-4" style={{ border: '1px solid #eee' }}>
        <div className="d-md-none d-flex justify-content-between align-items-center"
          style={{ cursor: 'pointer', marginBottom: filtersOpen ? 12 : 0 }}
          onClick={() => setFiltersOpen((v) => !v)}>
          <span className="fw-semibold" style={{ fontSize: 14 }}>Filtres de recherche</span>
          <span>{filtersOpen ? <FaChevronUp size={18} /> : <FaChevronDown size={18} />}</span>
        </div>
        <div className={filtersOpen ? '' : 'd-none d-md-block'}>
          <Row>
            <Col md={10} xs={12}>
              <Row className="gy-1 gx-3">
                <Col md={4} xs={12}>
                  <div>
                    <label className="small fw-semibold d-block mb-1">Plateforme</label>
                    <Form.Select size="sm" name="platform_type" value={filters.platform_type} onChange={handleFilterChange}>
                      <option value="">Toutes les plateformes</option>
                      {PLATFORM_OPTIONS.map((p) => (
                        <option key={p.value} value={p.value}>{p.label}</option>
                      ))}
                    </Form.Select>
                  </div>
                </Col>
                <Col md={4} xs={12}>
                  <div>
                    <label className="small fw-semibold d-block mb-1">Nom du canal</label>
                    <Form.Control size="sm" name="nom_canal" value={filters.nom_canal}
                      onChange={handleFilterChange} placeholder="Rechercher..." />
                  </div>
                </Col>
                <Col md={4} xs={12}>
                  <div>
                    <label className="small fw-semibold d-block mb-1">Statut</label>
                    <Form.Select size="sm" name="statut" value={filters.statut} onChange={handleFilterChange}>
                      <option value="">Tous les statuts</option>
                      <option value="active">Actif</option>
                      <option value="inactive">Inactif</option>
                    </Form.Select>
                  </div>
                </Col>
              </Row>
            </Col>
            <Col md={2} xs={12} className="d-flex flex-wrap align-items-end justify-content-md-end justify-content-center gap-2 mt-2 mt-md-0">
              {['platform_type', 'nom_canal', 'statut'].some((k) => filters[k]) && (
                <Button size="sm" variant="outline-secondary" onClick={handleClearFilters} className="text-uppercase px-2">
                  EFFACER
                </Button>
              )}
              {/* <Button size="sm" onClick={fetchData}
                style={{ backgroundColor: '#8b1512', border: 'none', letterSpacing: 1 }} className="text-uppercase px-2">
                RECHERCHER
              </Button> */}
            </Col>
          </Row>
        </div>
      </div>

      {/* Results count */}
      <div className="mb-3 d-flex justify-content-between align-items-center">
        <h6 style={{ fontWeight: 700, margin: 0 }}>Résultats ({pagination.total ?? data.length})</h6>
        {!isViewer && (
          <Button size="sm" style={{ backgroundColor: '#8b1512', border: 'none' }}
            onClick={() => { resetForm(); setShowAdd(true); }}>
            + Nouveau Canal
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" style={{ color: '#8b1512' }} /></div>
      ) : data.length === 0 ? (
        <div className="text-center py-5 text-secondary">Aucun canal trouvé.</div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="d-none d-md-block table-mcm-wrap" style={{ overflowX: 'auto' }}>
            <table className="table-mcm">
              <thead>
                <tr>
                  <th className="th-mcm">Icône</th>
                  <th className="th-mcm">Nom du canal</th>
                  <th className="th-mcm">Plateforme</th>
                  <th className="th-mcm">URL</th>
                  <th className="th-mcm">Description</th>
                  <th className="th-mcm">Statut</th>
                  {!isViewer && <th className="th-mcm">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {data.map((item) => {
                  const { Icon, bg, color } = getPlatform(item.platform_type);
                  const platform = getPlatform(item.platform_type);
                  return (
                    <tr key={item.id}>
                      <td className="td-mcm">
                        <div style={{ width: 36, height: 36, borderRadius: '50%', backgroundColor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          {item.icone_url
                            ? <img src={item.icone_url} alt="icon" style={{ width: 36, height: 36, objectFit: 'contain', borderRadius: '50%' }} />
                            : <Icon size={20} color={color} />
                          }
                        </div>
                      </td>
                      <td className="td-mcm" style={{ fontWeight: 600 }}>{item.nom_canal}</td>
                      <td className="td-mcm">
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 10px', borderRadius: 10, fontSize: 11, backgroundColor: bg, fontWeight: 600, color }}>
                          <platform.Icon size={12} /> {platform.label}
                        </span>
                      </td>
                      <td className="td-mcm">
                        <a href={item.url_connexion} target="_blank" rel="noreferrer"
                          style={{ color: '#8b1512', fontSize: 12 }}>
                          {item.url_connexion?.slice(0, 35)}{item.url_connexion?.length > 35 ? '…' : ''}
                        </a>
                      </td>
                      <td className="td-mcm" style={{ color: '#666' }}>
                        {item.description
                          ? item.description.length > 60
                            ? (
                              <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip>{item.description}</Tooltip>}
                              >
                                <span style={{ cursor: 'default' }}>
                                  {item.description.slice(0, 60)}…
                                </span>
                              </OverlayTrigger>
                            )
                            : item.description
                          : '—'}
                      </td>
                      <td className="td-mcm">
                        <span style={{
                          padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                          backgroundColor: item.statut === 'active' ? '#d4edda' : '#f8d7da',
                          color: item.statut === 'active' ? '#155724' : '#721c24',
                        }}>
                          {item.statut === 'active' ? 'Actif' : 'Inactif'}
                        </span>
                      </td>
                      {!isViewer && (
                        <td className="td-mcm">
                          <div className="d-flex gap-2">
                            <BsPencil size={15} style={{ cursor: 'pointer', color: '#555' }} title="Modifier" onClick={() => handleEditOpen(item)} />
                            <BsTrash size={15} style={{ cursor: 'pointer', color: '#8b1512' }} title="Supprimer" onClick={() => setDeleteTarget(item)} />
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="d-md-none">
            <div className="d-flex flex-column gap-3">
              {data.map((item) => {
                const { Icon, bg, color } = getPlatform(item.platform_type);
                const platform = getPlatform(item.platform_type);
                return (
                  <div key={item.id} className="bg-white rounded p-3" style={{ border: '1px solid #eee' }}>
                    {/* Card header */}
                    <div className="d-flex justify-content-between align-items-start border-bottom pb-3 mb-3">
                      <div className="d-flex align-items-center gap-2">
                        <div style={{ width: 40, height: 40, borderRadius: '50%', backgroundColor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          {item.icone_url
                            ? <img src={item.icone_url} alt="icon" style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: '50%' }} />
                            : <Icon size={22} color={color} />
                          }
                        </div>
                        <div>
                          <div className="fw-bold" style={{ fontSize: 14 }}>{item.nom_canal}</div>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '1px 8px', borderRadius: 10, fontSize: 11, backgroundColor: bg, fontWeight: 600, color }}>
                            <platform.Icon size={11} /> {platform.label}
                          </span>
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <span style={{
                          padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
                          backgroundColor: item.statut === 'active' ? '#d4edda' : '#f8d7da',
                          color: item.statut === 'active' ? '#155724' : '#721c24',
                        }}>
                          {item.statut === 'active' ? 'Actif' : 'Inactif'}
                        </span>
                        <div style={{ position: 'relative' }}>
                          {!isViewer && (
                            <>
                              <BsThreeDotsVertical
                                size={18} color="#555" style={{ cursor: 'pointer' }}
                                onClick={() => setOpenMenuId(openMenuId === item.id ? null : item.id)}
                              />
                              {openMenuId === item.id && (
                                <div style={{
                                  position: 'absolute', right: 0, top: '100%', zIndex: 100,
                                  backgroundColor: '#fff', border: '1px solid #e0e0e0',
                                  borderRadius: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                                  minWidth: 140, overflow: 'hidden', marginTop: 4,
                                }}>
                                  <button type="button"
                                    style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 14px', background: 'none', border: 'none', fontSize: 13, cursor: 'pointer' }}
                                    onClick={() => { handleEditOpen(item); setOpenMenuId(null); }}>
                                    <BsPencil size={13} color="#555" /> Modifier
                                  </button>
                                  <button type="button"
                                    style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 14px', background: 'none', border: 'none', fontSize: 13, color: '#8b1512', cursor: 'pointer' }}
                                    onClick={() => { setDeleteTarget(item); setOpenMenuId(null); }}>
                                    <BsTrash size={13} color="#8b1512" /> Supprimer
                                  </button>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* Card body */}
                    <div style={{ fontSize: 13 }}>
                      {item.description && (
                        <div className="mb-2 text-muted">{item.description}</div>
                      )}
                      <div>
                        <a href={item.url_connexion} target="_blank" rel="noreferrer"
                          style={{ color: '#8b1512', wordBreak: 'break-word' }}>
                          {item.url_connexion}
                        </a>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Pagination */}
      <Pagination
        currentPage={filters.page}
        totalPages={pagination.totalPages}
        onPageChange={(page) => setFilters((f) => ({ ...f, page }))}
      />

      {/* Edit Modal */}
      {!isViewer && (
        <ChannelModal
          show={!!editItem}
          title={`Modifier — ${editItem?.nom_canal || ''}`}
          form={form} setF={setF}
          formErrors={formErrors}
          iconFile={iconFile} iconPreview={iconPreview}
          fileRef={fileRef} handleIconChange={handleIconChange}
          saving={saving}
          uploadProgress={uploadProgress}
          onSave={handleSave}
          onClose={() => { setEditItem(null); resetForm(); }}
          isEdit
        />
      )}

      {/* Add Modal */}
      {!isViewer && (
        <ChannelModal
          show={showAdd}
          title="Ajouter un canal"
          form={form} setF={setF}
          formErrors={formErrors}
          iconFile={iconFile} iconPreview={iconPreview}
          fileRef={fileRef} handleIconChange={handleIconChange}
          saving={saving}
          uploadProgress={uploadProgress}
          onSave={handleSave}
          onClose={() => { setShowAdd(false); resetForm(); }}
        />
      )}

      <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)} centered>
        <Modal.Body style={{ padding: 28 }}>
          {/* <button onClick={() => setDeleteTarget(null)} className="modal-close-btn">×</button> */}
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <div style={{ width: 48, height: 48, borderRadius: '50%', backgroundColor: '#ffe5e5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>
              <BsTrash size={22} color="#8B0000" />
            </div>
            <h6 style={{ fontWeight: 700, marginBottom: 2 }}>Supprimer ce canal ?</h6>
            <p style={{ fontSize: 13, color: '#666', margin: 0 }}>Cette action est irréversible.</p>
          </div>
          {deleteTarget && (() => {
            const platform = getPlatform(deleteTarget.platform_type);
            return (
              <div style={{ backgroundColor: '#f7f8fa', borderRadius: 8, padding: '14px 16px', marginBottom: 24, fontSize: 13 }}>
                <div className="mb-2"><span className="fw-semibold">Nom : </span>{deleteTarget.nom_canal}</div>
                <div className="mb-2" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span className="fw-semibold">Plateforme : </span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '1px 8px', borderRadius: 10, fontSize: 11, backgroundColor: platform.bg, fontWeight: 600, color: platform.color }}>
                    <platform.Icon size={11} /> {platform.label}
                  </span>
                </div>
                <div className="mb-2"><span className="fw-semibold">Statut : </span>
                  <span style={{ padding: '1px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, backgroundColor: deleteTarget.statut === 'active' ? '#d4edda' : '#f8d7da', color: deleteTarget.statut === 'active' ? '#155724' : '#721c24' }}>
                    {deleteTarget.statut === 'active' ? 'Actif' : 'Inactif'}
                  </span>
                </div>
                <div style={{ marginTop: 8 }}>
                  <a href={deleteTarget.url_connexion} target="_blank" rel="noreferrer" style={{ color: '#8b1512', wordBreak: 'break-all', fontSize: 12 }}>
                    {deleteTarget.url_connexion}
                  </a>
                </div>
              </div>
            );
          })()}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button onClick={() => setDeleteTarget(null)} style={{ padding: '8px 24px', borderRadius: 4, border: '1px solid #ccc', background: '#fff', cursor: 'pointer', fontSize: 13, letterSpacing: 0.5 }}>ANNULER</button>
            <button onClick={handleDelete} style={{ padding: '8px 24px', borderRadius: 4, border: 'none', backgroundColor: '#8B0000', color: '#fff', cursor: 'pointer', fontSize: 13, letterSpacing: 0.5 }}>SUPPRIMER</button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}

function ChannelModal({ show, title, form, setF, formErrors, iconFile, iconPreview, fileRef, handleIconChange, saving, uploadProgress, onSave, onClose, isEdit }) {
  return (
    <Modal show={show} onHide={onClose} centered scrollable>
      <Modal.Header style={{ position: 'relative', padding: '16px 32px', borderBottom: '1px solid #f0f0f0' }}>
        <Modal.Title as="h6" style={{ fontWeight: 700, margin: 0 }}>{title}</Modal.Title>
        <button onClick={onClose} className="modal-close-btn" aria-label="Fermer">×</button>
      </Modal.Header>
      <Modal.Body style={{ padding: 32 }}>
        <Row className="g-3 mb-3">
          <Col xs={6}>
            <Form.Group>
              <Form.Label className="modal-label">Plateforme :</Form.Label>
              <Form.Select value={form.platform_type} onChange={(e) => setF('platform_type', e.target.value)} className="input-mcm" style={{ marginTop: 4 }}>
                {PLATFORM_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col xs={6}>
            <Form.Group>
              <Form.Label className="modal-label">Nom du canal :</Form.Label>
              <input className={`input-mcm${formErrors.nom_canal ? ' is-invalid' : ''}`} value={form.nom_canal} onChange={(e) => setF('nom_canal', e.target.value)} placeholder="Ministère Catholique Miséricorde" style={{ marginTop: 4 }} />
              {formErrors.nom_canal && <div className="text-danger" style={{ fontSize: 12, marginTop: 2 }}>{formErrors.nom_canal}</div>}
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="mb-3">
          <Form.Label className="modal-label">URL de connexion :</Form.Label>
          <input type='url' className={`input-mcm${formErrors.url_connexion ? ' is-invalid' : ''}`} value={form.url_connexion} onChange={(e) => setF('url_connexion', e.target.value)} placeholder="https://www.facebook.com/..." style={{ marginTop: 4 }} />
          {formErrors.url_connexion && <div className="text-danger" style={{ fontSize: 12, marginTop: 2 }}>{formErrors.url_connexion}</div>}
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="modal-label">Description :</Form.Label>
          <textarea className="input-mcm" rows={2} value={form.description} onChange={(e) => setF('description', e.target.value)} placeholder="(optionnel)" style={{ marginTop: 4, resize: 'vertical' }} />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="modal-label">Statut :</Form.Label>
          <Form.Select value={form.statut} onChange={(e) => setF('statut', e.target.value)} style={{ marginTop: 4 }}>
            <option value="active">Actif</option>
            <option value="inactive">Inactif</option>
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="modal-label">Icône (optionnelle) :</Form.Label>
          <div className="d-flex align-items-center gap-2 mt-1" style={{ border: '1px solid #ccc', borderRadius: 4, padding: '8px 12px', minWidth: 0 }}>
            {iconPreview ? (
              <img src={iconPreview} alt="icon" style={{ width: 36, height: 36, objectFit: 'contain', borderRadius: '50%', flexShrink: 0 }} />
            ) : (
              <div style={{ width: 36, height: 36, backgroundColor: '#f0f0f0', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <BsImage size={16} color="#aaa" />
              </div>
            )}
            <span
              className="flex-grow-1 text-truncate"
              title={iconFile?.name}
              style={{ minWidth: 0, fontSize: 13, color: iconFile ? '#333' : '#aaa' }}
            >
              {iconFile ? iconFile.name : isEdit ? 'Image actuelle' : 'Aucun fichier'}
            </span>
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleIconChange} />
            <Button variant="outline-secondary" size="sm" type="button" onClick={() => fileRef.current.click()}>
              {isEdit ? 'Remplacer' : 'Importer'} ⇅
            </Button>
          </div>
          {uploadProgress > 0 && uploadProgress < 100 && (
            <div className="mt-2">
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Téléchargement: {uploadProgress}%</div>
              <div style={{ height: 6, backgroundColor: '#f0f0f0', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ height: '100%', backgroundColor: '#8b1512', width: `${uploadProgress}%`, transition: 'width 0.3s' }} />
              </div>
            </div>
          )}
        </Form.Group>

        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <button className="btn-primary-mcm" onClick={onSave} disabled={saving} style={{ minWidth: 180, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            {saving ? <Spinner size="sm" animation="border" /> : isEdit ? 'ENREGISTRER LES MODIFICATIONS' : 'ENREGISTRER'}
          </button>
        </div>
      </Modal.Body>
    </Modal>
  );
}
